import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Loader2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { getErrorMessage } from '../lib/errors';
import { ThemeToggle, Logo } from '../components/Layout';
import { trackEvent, EVENTS } from '../lib/analytics';

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Where to send the user after signup succeeds. Callers pass `?next=`
  // to steer post-signup routing (e.g. the free-score CTA sends new
  // signups straight to /pricing so they can convert while the audit
  // is still fresh). Anchor to a safe internal path — reject external
  // URLs / protocol-relative values.
  const rawNext = searchParams.get('next') || '';
  const nextPath = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//')
    ? rawNext
    : '/dashboard';

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, name);
      trackEvent(EVENTS.SIGN_UP, { method: 'email', next: nextPath });
      toast.success('Welcome to Blayzrr!');
      navigate(nextPath);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <aside className="hidden lg:flex relative flex-col p-10 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 hero-mesh-light opacity-20" />
        <div className="relative">
          <Logo />
          <div className="mt-16">
            <h2 className="text-3xl font-bold leading-tight">Your LinkedIn, but sharper.</h2>
            <ul className="mt-6 space-y-3 max-w-md">
              <li className="flex gap-3"><span>✓</span> Profile rewrite aligned to your objective &amp; role</li>
              <li className="flex gap-3"><span>✓</span> 6 posts in your voice on trending topics</li>
              <li className="flex gap-3"><span>✓</span> Copy, regenerate, export to PDF / DOCX</li>
            </ul>
          </div>
        </div>
        <p className="relative mt-auto text-sm opacity-80">No card required. Cancel anytime.</p>
      </aside>
      <main className="flex flex-col">
        <div className="flex items-center justify-between p-4 lg:hidden">
          <Logo />
          <ThemeToggle />
        </div>
        <div className="hidden lg:flex justify-end p-4">
          <ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <Card className="w-full max-w-md border-border">
            <CardContent className="p-7">
              <h1 className="text-2xl font-bold">Create your account</h1>
              <p className="text-sm text-muted-foreground mt-1">Start crafting your Blayzrr.</p>

              {/* Same-email notice for buyers who paid via the guest
                  checkout — critical to avoid duplicate accounts. If the
                  email here differs from the one used at payment, we'll
                  spin up a *second* (unpaid) account and the buyer will
                  wonder where their plan went. */}
              <div
                className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-start gap-2.5"
                data-testid="signup-paid-notice"
              >
                <Info size={16} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <div className="text-[12px] leading-relaxed text-foreground/90">
                  <span className="font-semibold text-primary">Already paid?</span>{' '}
                  Use the <span className="font-semibold">exact same email</span> you entered at
                  Razorpay checkout. We&apos;ve already created an account for you and emailed
                  login credentials — sign in from the{' '}
                  <Link to="/login" className="underline underline-offset-2 font-semibold">
                    login page
                  </Link>
                  {' '}instead of creating a duplicate account here.
                </div>
              </div>

              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Avery Chen"
                      className="pl-9 h-11"
                      data-testid="signup-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-9 h-11"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="pl-9 h-11"
                      data-testid="auth-password-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 rounded-xl" disabled={loading} data-testid="auth-submit-button">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : 'Create account'}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                Already on Blayzrr?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="auth-switch-to-login">
                  Log in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
